import {
  COLLABORATORS_HOME_ROUTE,
  ADD_COLLABORATORS_ROUTE,
  DELETE_COLLABORATORS_ROUTE,
  COLLABORATORS_ADD_DEPENDENTS_ROUTE
} from './auth';
import { ADD_PERSON, CHECK_CIRCLE_V2, CLOCK_ICON, EMAIL_ICON, PERSON_MULTIPLE, REMOVE_PERSON } from './images';
import { PERMISSIONS } from './permissions';
import { FILTER_ACTIVE, FILTER_ALL, FILTER_INVITED } from './tagManager';
import { ReactComponent as ExcitedBuddy } from '../../assets/icons/buddy-excited.svg';
import { ReactComponent as SadBuddy } from '../../assets/icons/buddy-sad.svg';
import { IButtonImageV3 } from '../interfaces/button';
import { IMembersFilter } from '../interfaces/collaboratorFilter';
import { IDropzoneValidator } from '../interfaces/fileUploaderContainer';
import { TRANSLATION_COLLABORATORS } from '../translations/collaborators';
import { TRANSLATIONS_GLOBAL } from '../translations/commons';
import { TRANSLATION_MANAGE_COLLABORATORS } from '../translations/manageCollaborators';
import { BdsLabelPillVariant } from '../types/types';

export const MAX_COLLABORATORS = 5000;

export const COLLABORATORS_NE_OPTION_MENU = [
  {
    id: 0,
    label: TRANSLATIONS_GLOBAL.collaborators.details.index,
    tooltipText: TRANSLATIONS_GLOBAL.collaborators.details.tooltip,
    permissionsRequired: [PERMISSIONS['MENU_ROOT']['COLLABORATORS_ROOT']['DETAIL']],
    pathName: COLLABORATORS_HOME_ROUTE
  }
];

export const COLLABORATOR_STATUS = {
  active: 'active',
  inactive: 'inactive',
  invited: 'invited',
  inProgress: 'in_progress'
};

export const MEMBERS_OPTION_FILTER: { [key: string]: IMembersFilter } = {
  all: {
    gtmTag: FILTER_ALL,
    status: undefined
  },
  active: {
    gtmTag: FILTER_ACTIVE,
    status: COLLABORATOR_STATUS.active
  },
  invited: {
    gtmTag: FILTER_INVITED,
    status: COLLABORATOR_STATUS.invited
  }
};

export const INVITE_COLLABORATORS_MODAL: {
  title: string;
  subtitle: string;
  primaryButton: string;
  primaryButtonAddDomains: string;
  primaryButtonConfiguredDomins: string;
} = {
  title: TRANSLATIONS_GLOBAL.modals.inviteCollaborators.title,
  subtitle: TRANSLATIONS_GLOBAL.modals.inviteCollaborators.subtitle,
  primaryButton: TRANSLATIONS_GLOBAL.modals.inviteCollaborators.primaryButtonText,
  primaryButtonAddDomains: TRANSLATIONS_GLOBAL.modals.inviteCollaborators.primaryButtonAddDomains,
  primaryButtonConfiguredDomins: TRANSLATIONS_GLOBAL.modals.inviteCollaborators.primaryButtonConfiguredDomins
};

export const INVITE_COLLABORATORS_ERRORS_MODAL: {
  title: string;
  subtitle: string;
  primaryButton: string;
} = {
  title: TRANSLATIONS_GLOBAL.modals.inviteCollaboratorsErrors.title,
  subtitle: TRANSLATIONS_GLOBAL.modals.inviteCollaboratorsErrors.subtitle,
  primaryButton: TRANSLATIONS_GLOBAL.modals.inviteCollaboratorsErrors.primaryButtonText
};

export const INVITE_COLLABORATORS_TABS = [
  TRANSLATIONS_GLOBAL.modals.inviteCollaborators.onlyEmailTab,
  TRANSLATIONS_GLOBAL.modals.inviteCollaborators.fileTab,
  TRANSLATIONS_GLOBAL.modals.inviteCollaborators.domainTab
];

export const SUCCESS_ADD_COLLABORATORS_MODAL = {
  ImageComponent: ExcitedBuddy,
  title: TRANSLATIONS_GLOBAL.modals.inviteCollaboratorsSuccess.title,
  description: TRANSLATIONS_GLOBAL.modals.inviteCollaboratorsSuccess.description,
  firstButtonText: TRANSLATIONS_GLOBAL.modals.inviteCollaboratorsSuccess.secondaryButtonText
};

export const ERROR_ADD_COLLABORATORS_MODAL = {
  ImageComponent: SadBuddy,
  title: TRANSLATIONS_GLOBAL.modals.inviteCollaboratorsError.title,
  description: TRANSLATIONS_GLOBAL.modals.inviteCollaboratorsError.description,
  firstButtonText: TRANSLATIONS_GLOBAL.modals.inviteCollaboratorsSuccess.secondaryButtonText
};

export const SUCCESS_DOMAIN_ADD_COLLABORATORS_MODAL = {
  ImageComponent: ExcitedBuddy,
  title: TRANSLATIONS_GLOBAL.modals.inviteCollaboratorsByDomainSuccess.title,
  description: TRANSLATIONS_GLOBAL.modals.inviteCollaboratorsByDomainSuccess.description,
  secondaryDescription: TRANSLATIONS_GLOBAL.modals.inviteCollaboratorsByDomainSuccess.secondaryDescription,
  firstButtonText: TRANSLATIONS_GLOBAL.modals.inviteCollaboratorsSuccess.secondaryButtonText
};

export const INVITE_COLLABORATORS_UPLOADER_VALIDATORS: IDropzoneValidator = {
  maxFileCount: 1,
  maxFileSize: 6291456, // 6MB
  fileFormats: {
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
  }
};

export const MAX_ERROR_ROWS_INVITE_BY_FILE = 50;

export const INVITE_COLLABORATORS_ERRORS = {
  MAX_FILE_SIZE: 54,
  DOMAIN_ALREADY_ASSIGNED: 92,
  GENERIC_DOMAINS_NOT_ALLOWED: 93,
  MAX_LICENSES_REACHED: 99
};

export const COLLABORATORS_TABLE_INITIAL_SORT = [true, false, false, false, false, false];

export const MANAGE_COLLABORATORS_OPTIONS = {
  addCollaborators: {
    value: 'addCollaborators',
    route: ADD_COLLABORATORS_ROUTE
  },
  deleteSubscriptions: {
    value: 'deleteSubscriptions',
    route: DELETE_COLLABORATORS_ROUTE
  },
  addDependents: {
    value: 'addDependents',
    route: COLLABORATORS_ADD_DEPENDENTS_ROUTE
  }
};

export const MANAGE_COLLABORATORS_CARDS = [
  {
    title: TRANSLATION_MANAGE_COLLABORATORS.manageCollaboratorsOptions.addCollaboratorsCard.title,
    description: TRANSLATION_MANAGE_COLLABORATORS.manageCollaboratorsOptions.addCollaboratorsCard.description,
    value: MANAGE_COLLABORATORS_OPTIONS.addCollaborators.value,
    pictogram: { component: ADD_PERSON }
  },
  {
    title: TRANSLATION_MANAGE_COLLABORATORS.manageCollaboratorsOptions.deleteCollaboratorsCard.title,
    description: TRANSLATION_MANAGE_COLLABORATORS.manageCollaboratorsOptions.deleteCollaboratorsCard.description,
    value: MANAGE_COLLABORATORS_OPTIONS.deleteSubscriptions.value,
    pictogram: { component: REMOVE_PERSON }
  }
];

export const MANAGE_COLLABORATORS_WITH_DEPENDENTS_CARDS = {
  title: TRANSLATION_MANAGE_COLLABORATORS.manageCollaboratorsOptions.addDependentsCard.title,
  description: TRANSLATION_MANAGE_COLLABORATORS.manageCollaboratorsOptions.addDependentsCard.description,
  value: MANAGE_COLLABORATORS_OPTIONS.addDependents.value,
  pictogram: { component: PERSON_MULTIPLE }
};

export const COLLABORATORS_TABS = [
  {
    title: TRANSLATION_COLLABORATORS.hero.tabs.collaborators,
    type: 'collaborators'
  },
  {
    title: TRANSLATION_COLLABORATORS.hero.tabs.suscriptionsDeleted,
    type: 'deleted'
  },
  {
    title: TRANSLATION_COLLABORATORS.hero.tabs.toAdd,
    type: 'toAdd'
  }
];

export const COLLABORATORS_TABLE_ACTIONS = {
  resendEmail: 'resendEmail',
  delete: 'delete',
  invitedStatus: 'invited',
  isInvited: 'hasInvited'
};

export const COLLABORATORS_STATUS: {
  [key: string]: { tagVariant: BdsLabelPillVariant; icon: IButtonImageV3 };
} = {
  active: { tagVariant: 'success', icon: CHECK_CIRCLE_V2 },
  invited: { tagVariant: 'info', icon: EMAIL_ICON },
  in_progress: { tagVariant: 'inactive', icon: CLOCK_ICON },
  pending: { tagVariant: 'inactive', icon: CLOCK_ICON }
};

export const COLLABORATORS_DETAILS_STATUS: {
  [key: string]: { tagVariant: BdsLabelPillVariant; icon: IButtonImageV3 };
} = {
  active: {
    tagVariant: 'success-inverse',
    icon: CHECK_CIRCLE_V2
  },
  invited: {
    tagVariant: 'info-inverse',
    icon: EMAIL_ICON
  },
  in_progress: {
    tagVariant: 'inactive-inverse',
    icon: CLOCK_ICON
  },
  pending: {
    tagVariant: 'inactive-inverse',
    icon: CLOCK_ICON
  }
};
